import { TextInput } from "./text-input";
import { Icons } from "components/icons";
import { IconButton, InputBaseProps } from "components";
import { FC, useEffect, useRef, useState } from "react";
import { COLOR_PALETTE } from "theme";
import jmoment from "jalali-moment";
import classNames from "classnames";

interface props extends InputBaseProps {
  value?: string;
  setValue?: (v: string) => void;
}

export const DateInput: FC<props> = (props) => {
  const yearsContainerRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [yearsCollapseOpen, setYearsCollapseOpen] = useState(false);
  const [value, setValue] = useState<string | null>(null);

  const currentDate = value
    ? jmoment(value).startOf("days")
    : props.value
    ? jmoment(props.value).startOf("days")
    : jmoment().startOf("days");

  const jCurrentDate = currentDate.clone().locale("fa");

  const jCurrentDay = jCurrentDate.format("DD");
  const jCurrentYear = jCurrentDate.format("YYYY");
  const jCurrentMonth = jCurrentDate.format("MM");

  const startOfMonth =
    jCurrentDate.startOf("month").day() + 1 > 7
      ? jCurrentDate.startOf("month").day() + 1 - 7
      : jCurrentDate.startOf("month").day() + 1;

  const goNextMonth = () => {
    const newDate = jmoment(currentDate ?? "").add(1, "jmonth");

    if (!isDisable(newDate.locale("fa").format("YYYY/MM/DD HH:mm"))) {
      setValue(newDate.locale("en").format("YYYY/MM/DD HH:mm"));
    }
  };

  const goPrevMonth = () => {
    const newDate = jmoment(currentDate ?? "").subtract(1, "jmonth");

    if (!isDisable(newDate.locale("fa").format("YYYY/MM/DD HH:mm"))) {
      setValue(newDate.locale("en").format("YYYY/MM/DD HH:mm"));
    }
  };

  const selectYear = (value: number) => {
    const validValue =
      value === 1278
        ? `${value}/${10}/${11}`
        : value === 1477
        ? `${value}/${10}/${8}`
        : `${value}/${jCurrentMonth}/${jCurrentDay}`;

    setValue(
      jmoment
        .from(validValue, "fa", "YYYY/MM/DD")
        .locale("en")
        .format("YYYY/MM/DD HH:mm")
    );
    setYearsCollapseOpen(false);
  };

  const isDisable = (value: string) => {
    return (
      jmoment(value)
        .locale("fa")
        .isBefore(jmoment("1278/10/11").locale("fa")) ||
      jmoment(value).locale("fa").isAfter(jmoment("1477/10/8").locale("fa"))
    );
  };

  useEffect(() => {
    if (!yearsCollapseOpen) {
      const scrollValue = (Number(jCurrentYear) - 1268) * 10;
      yearsContainerRef.current?.scrollTo({ top: scrollValue });
    }
  }, [jCurrentYear]);

  return (
    <div>
      <TextInput
        {...props}
        inputProps={{
          value: props.value
            ? jmoment(props.value)
                .startOf("days")
                .locale("fa")
                .format("DD MMM YYYY")
            : "",
          readOnly: true,
          onClick: () => setOpen(true),
        }}
        endComponent={
          <div className="px-2">
            <Icons name="Calendar" color={COLOR_PALETTE.primary} />
          </div>
        }
      />
      <dialog
        className="modal modal-top bg-[#333333CC]"
        open={open}
        onClose={() => setOpen(false)}
        onClick={() => setOpen(false)}
      >
        <div
          className="modal-box max-w-[320px] mx-auto p-3 bg-transparent shadow-none"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-2xl py-4">
            <div className="flex items-center justify-between h-8 mb-3 px-4">
              <div
                onClick={() => setYearsCollapseOpen((prev) => !prev)}
              >{`${jCurrentDate.format("MMM")} ${jCurrentYear}`}</div>
              {!yearsCollapseOpen && (
                <div className="flex items-center gap-2">
                  <IconButton
                    size="xs"
                    iconName="AngleRight"
                    backgroundColor="transparent"
                    contentColor="#555"
                    buttonProps={{ onClick: goPrevMonth }}
                  />
                  <IconButton
                    size="xs"
                    iconName="AngleLeft"
                    backgroundColor="transparent"
                    contentColor="#555"
                    buttonProps={{ onClick: goNextMonth }}
                  />
                </div>
              )}
            </div>
            <div className="relative flex flex-col items-center h-72">
              <div
                className={classNames(
                  "absolute inset-x-0 top-0 collapse bg-white",
                  {
                    "collapse-open": yearsCollapseOpen,
                    "collapse-close": !yearsCollapseOpen,
                  }
                )}
              >
                <div className="collapse-content p-0">
                  <div
                    ref={yearsContainerRef}
                    className="max-h-72 overflow-y-scroll py-4"
                  >
                    <div className="grid grid-cols-4 gap-y-4">
                      {Array.from(Array(200), (_, i) => (
                        <button
                          className={classNames("btn btn-ghost btn-sm", {
                            "bg-primary": Number(jCurrentYear) === 1278 + i,
                          })}
                          onClick={() => selectYear(i + 1278)}
                        >
                          {i + 1278}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="max-w-[280px] grid grid-cols-7 px-4">
                {jmoment
                  .updateLocale("fa", {
                    weekdaysMin: ["ش", "ی", "د", "س", "چ", "پ", "ج"],
                  })
                  .weekdaysMin()
                  .map((day) => (
                    <div className="flex w-9 h-10 items-center text-center justify-center mx-0.5 text-sm">
                      {day}
                    </div>
                  ))}
              </div>
              <div className="max-w-[280px] grid grid-cols-7 gap-1 px-4">
                {Array.from(Array(startOfMonth), () => (
                  <div />
                ))}
                {Array.from(Array(jCurrentDate.jDaysInMonth()), (_, i) => (
                  <button
                    disabled={isDisable(
                      `${jCurrentYear}/${jCurrentMonth}/${1 + i}`
                    )}
                    className={classNames(
                      "btn btn-circle btn-sm btn-ghost w-9 h-9 disabled:bg-white",
                      {
                        "bg-primary": Number(jCurrentDay) === 1 + i,
                      }
                    )}
                    onClick={() => {
                      setValue(
                        jmoment
                          .from(
                            `${jCurrentYear}/${jCurrentMonth}/${1 + i}`,
                            "fa",
                            "YYYY/MM/DD"
                          )
                          .locale("en")
                          .format("YYYY/MM/DD HH:mm")
                      );
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end px-4 gap-2">
              <button
                className="btn btn-ghost btn-sm text-gray-400"
                onClick={() => setOpen(false)}
              >
                انصراف
              </button>
              <button
                className="btn btn-ghost btn-sm text-gray-400"
                onClick={() => {
                  setOpen(false);
                  !!props.setValue &&
                    props.setValue(
                      value ?? currentDate.format("YYYY/MM/DD HH:mm")!
                    );
                }}
              >
                تایید
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};
